import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import rrService from '../services/rrService';
import SearchableDropdown from '../components/SearchableDropdown';
import SortableResizableTable from '../components/SortableResizableTable';
import SuggestedTradesTable from '../components/SuggestedTradesTable';
import PortfolioSummaryPieChart from '../components/PortfolioSummaryPieChart';
import CashSummaryCard from '../components/CashSummaryCard';
import AllocationVisualization from '../components/AllocationVisualization';
import AccountAllocationBars from '../components/AccountAllocationBars';
import DirectedTradeReportsModal from '../components/DirectedTradeReportsModal';
import '../styles/SelectStyles.css';

/**
 * Mode options keyed by action. Each entry has a backend `value` and a
 * human-readable `label`/`help` for the dropdown.
 */
const BUY_MODES = [
  { value: 'to_target', label: 'Buy to target weight', help: 'Buy up to the model target weight (skips accounts already at/above target).' },
  { value: 'to_max', label: 'Buy to maximum target weight', help: 'Buy up to the model maximum weight.' },
  { value: 'to_min', label: 'Buy to minimum target weight', help: 'Buy up to the model minimum weight (skips accounts already at/above min).' },
];

const SELL_MODES = [
  { value: 'to_target', label: 'Sell to target weight', help: 'Sell down to the model target weight. Or Sell out if no longer in the model' },
  { value: 'sell_out', label: 'Sell out (entire position)', help: 'Sell the full position in every account that holds the symbol.' },
  { value: 'to_min', label: 'Sell to minimum target weight', help: 'Sell down to the model minimum weight.' },
  { value: 'to_max', label: 'Sell to maximum target weight', help: 'Sell down to the model maximum weight.' },
];

const BUY_CASH_POLICIES = [
  {
    value: 'above_max_reserves',
    label: 'Spend only cash above max reserve',
    help: 'Most conservative. Keeps each account at or above Calculated Reserve Max.',
  },
  {
    value: 'above_target_reserves',
    label: 'Spend only cash above target reserve',
    help: 'Balanced default. Keeps each account at or above Calculated Reserve Center.',
  },
  {
    value: 'above_min_reserve',
    label: 'Spend only cash above min reserve',
    help: 'More aggressive. Allows spending down to Calculated Reserve Min.',
  },
  {
    value: 'all_cash',
    label: 'Spend all available cash',
    help: 'Most aggressive. Allows spending cash down to $0.',
  },
];

const CASH_LIKE_SYMBOLS = ['FCASH', 'SPAXX', 'SWVXX', 'VMFXX', 'FDRXX', 'SPRXX', 'FZFXX', 'SNSXX', 'SNVXX'];

const isCashLikeHolding = (holding) => {
  const symbol = (holding?.Symbol || holding?.symbol || '').toUpperCase();
  const name = (holding?.['Security Level Model Name'] || holding?.modelName || '').toUpperCase();
  return CASH_LIKE_SYMBOLS.includes(symbol) || name === 'UNASSIGNED CASH' || name === 'CASH';
};

const getHoldingValue = (holding) => (
  holding?.['Holdings Current Value']
  ?? holding?.holdingsCurrentValue
  ?? holding?.value
  ?? 0
);

const getHoldingSymbol = (holding) => holding?.Symbol || holding?.symbol || '';

const getHoldingModelName = (holding) => {
  const rawModelName = holding?.['Security Level Model Name'] || holding?.modelName || '';
  return rawModelName && rawModelName.trim() !== '' ? rawModelName : 'Other Holdings';
};

const getHoldingSleeveName = (holding) => {
  const rawSleeveName = holding?.['Sleeve Name'] || holding?.sleeveName || holding?.subModelName || '';
  return rawSleeveName && rawSleeveName.trim() !== '' ? rawSleeveName : 'Other Holdings';
};

const getGroupModelName = (group) => {
  const accountAllocations = group?.detail?.account_allocations || group?.detail?.accountAllocations || [];
  const modelCounts = accountAllocations.reduce((acc, allocation) => {
    const model = (allocation?.modelName || allocation?.model_name || '').toString().trim();
    if (!model) return acc;
    acc[model] = (acc[model] || 0) + 1;
    return acc;
  }, {});

  const accountModels = Object.keys(modelCounts);
  if (accountModels.length === 1) return accountModels[0];
  if (accountModels.length > 1) {
    return accountModels.sort((a, b) => (modelCounts[b] || 0) - (modelCounts[a] || 0))[0];
  }

  const explicitModel = group?.account_model_name || group?.accountModelName || group?.model_name || group?.modelName || '';
  return (explicitModel || '').toString().trim();
};

const getTradeableAccountAllocations = (group) => {
  const accountAllocations = group?.detail?.account_allocations || group?.detail?.accountAllocations || [];
  return accountAllocations.filter((allocation) => {
    if (typeof allocation?.isTradeable === 'boolean') {
      return allocation.isTradeable;
    }
    const accountName = (allocation?.accountName || allocation?.account_name || '').toString().toUpperCase();
    return !['PARAMETRIC', 'BELLE HAVEN', 'BELLEHAVEN'].some((marker) => accountName.includes(marker));
  });
};

const getCashRelevantAccountAllocations = (group) => {
  const tradeableAllocations = getTradeableAccountAllocations(group);
  const buyEligibleAllocations = tradeableAllocations.filter(
    (allocation) => allocation?.isBuyCashEligible === true
  );
  if (buyEligibleAllocations.length > 0) {
    return buyEligibleAllocations;
  }
  return tradeableAllocations;
};

const getGroupStartingCash = (group) => {
  const cashRelevantAccountAllocations = getCashRelevantAccountAllocations(group);
  return cashRelevantAccountAllocations.reduce((sum, allocation) => {
    const cashValue = allocation?.cashCurrent ?? allocation?.cash_current ?? 0;
    const numericCash = Number(cashValue);
    // Floor each account's cash at 0 so margin (negative net cash) is excluded.
    return sum + (Number.isFinite(numericCash) ? Math.max(0, numericCash) : 0);
  }, 0);
};

const getReserveFloorForAccount = (account, buyCashPolicy) => {
  const reserveMin = Number(
    account?.cashReserveMin ?? account?.cash_reserve_min ?? 0
  );
  const reserveCenter = Number(
    account?.cashReserveCenter ?? account?.cash_reserve_center ?? 0
  );
  const reserveMax = Number(
    account?.cashReserveMax ?? account?.cash_reserve_max ?? 0
  );

  if (buyCashPolicy === 'above_max_reserves') {
    return Number.isFinite(reserveMax) ? reserveMax : 0;
  }
  if (buyCashPolicy === 'above_min_reserve') {
    return Number.isFinite(reserveMin) ? reserveMin : 0;
  }
  if (buyCashPolicy === 'all_cash') {
    return 0;
  }
  return Number.isFinite(reserveCenter) ? reserveCenter : 0;
};

const getGroupReservedCash = (group, buyCashPolicy) => {
  const cashRelevantAccountAllocations = getCashRelevantAccountAllocations(group);
  return cashRelevantAccountAllocations.reduce((sum, allocation) => {
    const cashValue = allocation?.cashCurrent ?? allocation?.cash_current ?? 0;
    const numericCash = Number(cashValue);
    const startingCash = Number.isFinite(numericCash) ? Math.max(0, numericCash) : 0;
    const reserveFloor = Math.max(0, getReserveFloorForAccount(allocation, buyCashPolicy));
    return sum + Math.min(startingCash, reserveFloor);
  }, 0);
};

const getGroupSpendableCash = (group, buyCashPolicy) => {
  const startingCash = getGroupStartingCash(group);
  const reservedCash = getGroupReservedCash(group, buyCashPolicy);
  return Math.max(0, startingCash - reservedCash);
};

const getGroupMaxAccountSpendableCash = (group, buyCashPolicy) => {
  const cashRelevantAccountAllocations = getCashRelevantAccountAllocations(group);
  return cashRelevantAccountAllocations.reduce((maxSpendable, allocation) => {
    const cashValue = allocation?.cashCurrent ?? allocation?.cash_current ?? 0;
    const numericCash = Number(cashValue);
    const startingCash = Number.isFinite(numericCash) ? Math.max(0, numericCash) : 0;
    const reserveFloor = Math.max(0, getReserveFloorForAccount(allocation, buyCashPolicy));
    const spendable = Math.max(0, startingCash - reserveFloor);
    return Math.max(maxSpendable, spendable);
  }, 0);
};

const areTradesEqual = (prevTrades = [], nextTrades = []) => {
  if (prevTrades === nextTrades) return true;
  if (!Array.isArray(prevTrades) || !Array.isArray(nextTrades)) return false;
  if (prevTrades.length !== nextTrades.length) return false;

  for (let i = 0; i < prevTrades.length; i += 1) {
    const prev = prevTrades[i] || {};
    const next = nextTrades[i] || {};
    if ((prev.account_number || prev.AccountNumber || '') !== (next.account_number || next.AccountNumber || '')) return false;
    if ((prev.symbol || prev.Symbol || '') !== (next.symbol || next.Symbol || '')) return false;
    if ((prev.action || prev.Action || '') !== (next.action || next.Action || '')) return false;
    if ((prev.quantity || 0) !== (next.quantity || 0)) return false;
    if ((prev.price || 0) !== (next.price || 0)) return false;
    if ((prev.trade_value || prev.value || 0) !== (next.trade_value || next.value || 0)) return false;
  }

  return true;
};

const areAllocationRowsEqual = (prevRows = [], nextRows = []) => {
  if (prevRows === nextRows) return true;
  if (!Array.isArray(prevRows) || !Array.isArray(nextRows)) return false;
  if (prevRows.length !== nextRows.length) return false;

  for (let i = 0; i < prevRows.length; i += 1) {
    const prev = prevRows[i] || {};
    const next = nextRows[i] || {};
    if ((prev.name || prev.sleeveName || '') !== (next.name || next.sleeveName || '')) return false;
    if ((prev.beforeValue || prev.before_value || 0) !== (next.beforeValue || next.before_value || 0)) return false;
    if ((prev.afterValue || prev.after_value || 0) !== (next.afterValue || next.after_value || 0)) return false;
    if ((prev.tradeImpact || prev.trade_impact || 0) !== (next.tradeImpact || next.trade_impact || 0)) return false;
    if ((prev.beforeWeight || prev.before_weight || 0) !== (next.beforeWeight || next.before_weight || 0)) return false;
    if ((prev.afterWeight || prev.after_weight || 0) !== (next.afterWeight || next.after_weight || 0)) return false;
  }

  return true;
};

const buildAllocationGroups = (allAccountHoldings = {}, trades = []) => {
  // Flatten holdings while preserving the account number for each holding
  // so that per-account trade impacts can be looked up correctly.
  const flattenedHoldings = [];
  Object.entries(allAccountHoldings || {}).forEach(([accountNumber, holdings]) => {
    if (!Array.isArray(holdings)) return;
    holdings.forEach((holding) => {
      if (!holding || isCashLikeHolding(holding)) return;
      const value = getHoldingValue(holding);
      if (value <= 0) return;
      flattenedHoldings.push({ ...holding, _accountNumber: accountNumber });
    });
  });

  const totalBeforeValue = flattenedHoldings.reduce((sum, holding) => sum + getHoldingValue(holding), 0);
  if (totalBeforeValue <= 0) {
    return { security: [], sleeve: [] };
  }

  // Build per-account+symbol trade impact to avoid multiplying a single
  // symbol's firm-wide impact across every account that holds it.
  const tradeImpactByAccountSymbol = (trades || []).reduce((acc, trade) => {
    const accountNumber = (trade.account_number || trade.AccountNumber || '').toString();
    const symbol = (trade.symbol || trade.Symbol || '').toUpperCase();
    if (!symbol) return acc;
    const absValue = Math.abs(trade.trade_value || trade.value || 0);
    const tradeAction = (trade.action || trade.Action || '').toUpperCase();
    const signedImpact = tradeAction === 'BUY' ? absValue : tradeAction === 'SELL' ? -absValue : 0;
    const key = `${accountNumber}::${symbol}`;
    acc[key] = (acc[key] || 0) + signedImpact;
    return acc;
  }, {});

  // Compute a single total-after denominator for consistent afterWeight across groups.
  const totalTradeImpact = (trades || []).reduce((sum, trade) => {
    const absValue = Math.abs(trade.trade_value || trade.value || 0);
    const tradeAction = (trade.action || trade.Action || '').toUpperCase();
    const signedImpact = tradeAction === 'BUY' ? absValue : tradeAction === 'SELL' ? -absValue : 0;
    return sum + signedImpact;
  }, 0);
  const totalAfterValue = Math.max(totalBeforeValue + totalTradeImpact, 1);

  const bySecurity = {};
  const bySleeve = {};

  flattenedHoldings.forEach((holding) => {
    const accountNumber = holding._accountNumber || '';
    const symbol = getHoldingSymbol(holding).toUpperCase();
    const modelName = getHoldingModelName(holding);
    const sleeveName = getHoldingSleeveName(holding);
    const beforeValue = getHoldingValue(holding);
    const tradeImpact = tradeImpactByAccountSymbol[`${accountNumber}::${symbol}`] || 0;
    const afterValue = beforeValue + tradeImpact;

    if (!bySecurity[modelName]) {
      bySecurity[modelName] = {
        name: modelName,
        beforeValue: 0,
        afterValue: 0,
        targetValue: 0,
        tradeImpact: 0,
      };
    }
    bySecurity[modelName].beforeValue += beforeValue;
    bySecurity[modelName].afterValue += afterValue;
    bySecurity[modelName].tradeImpact += tradeImpact;

    if (!bySleeve[sleeveName]) {
      bySleeve[sleeveName] = {
        name: sleeveName,
        beforeValue: 0,
        afterValue: 0,
        targetValue: 0,
        tradeImpact: 0,
      };
    }
    bySleeve[sleeveName].beforeValue += beforeValue;
    bySleeve[sleeveName].afterValue += afterValue;
    bySleeve[sleeveName].tradeImpact += tradeImpact;
  });

  const security = Object.values(bySecurity)
    .sort((a, b) => b.beforeValue - a.beforeValue)
    .map((item) => ({
      ...item,
      beforeWeight: (item.beforeValue / totalBeforeValue) * 100,
      afterWeight: (Math.max(item.afterValue, 0) / totalAfterValue) * 100,
      targetWeight: (item.beforeValue / totalBeforeValue) * 100,
    }));

  const sleeve = Object.values(bySleeve)
    .sort((a, b) => b.beforeValue - a.beforeValue)
    .map((item) => ({
      ...item,
      beforeWeight: (item.beforeValue / totalBeforeValue) * 100,
      afterWeight: (Math.max(item.afterValue, 0) / totalAfterValue) * 100,
      targetWeight: (item.beforeValue / totalBeforeValue) * 100,
    }));

  return { security, sleeve };
};

/**
 * DirectedTrade - Run a directed buy/sell of a single symbol firm-wide.
 *
 * Mirrors the Rebalancer Dashboard UX: a summary table of account
 * families with trade counts and values; clicking a row opens the full
 * per-group trade detail. Trades can be queued into a dedicated Directed
 * Trade queue and downloaded as custodian order files.
 */
const DirectedTrade = () => {
  // Symbol universe
  const [symbols, setSymbols] = useState([]);
  const [symbolsLoading, setSymbolsLoading] = useState(true);
  const [symbolsError, setSymbolsError] = useState('');
  const [bulkReportOptions, setBulkReportOptions] = useState([]);
  const [selectedBulkReports, setSelectedBulkReports] = useState([]);
  const [bulkReportsLoading, setBulkReportsLoading] = useState(true);
  const [bulkReportsError, setBulkReportsError] = useState('');

  // Trade inputs
  const [symbol, setSymbol] = useState('');
  const [action, setAction] = useState('buy');
  const [mode, setMode] = useState(BUY_MODES[0].value);
  const [buyCashPolicy, setBuyCashPolicy] = useState(
    'above_target_reserves'
  );

  // Run state
  const [result, setResult] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [runError, setRunError] = useState('');
  const [runProgress, setRunProgress] = useState('');
  const [isFetchingBulkReports, setIsFetchingBulkReports] = useState(false);
  const [bulkFetchError, setBulkFetchError] = useState('');
  const [bulkFetchStatus, setBulkFetchStatus] = useState(null);
  const [showBulkReportsModal, setShowBulkReportsModal] = useState(false);
  const [activeResultsTab, setActiveResultsTab] = useState('trades');
  const [resultsSearch, setResultsSearch] = useState('');

  // Detail modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  // Queue state: map of group_name -> queued item
  const [queuedTrades, setQueuedTrades] = useState({});
  const [isQueuingTrades, setIsQueuingTrades] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [allocationViewType, setAllocationViewType] = useState('sleeve');
  const [allocationOverrides, setAllocationOverrides] = useState({});
  const [liveTradesOverrides, setLiveTradesOverrides] = useState({});
  const [groupBaseAccounts, setGroupBaseAccounts] = useState({});
  const [groupBaseHoldings, setGroupBaseHoldings] = useState({});
  const queueTradesTriggerRef = useRef(null);
  const loadingGroupDataRef = useRef(new Set());
  const loadedGroupDataRef = useRef(new Set());

  const modeOptions = action === 'buy' ? BUY_MODES : SELL_MODES;
  const selectedBuyCashPolicy = BUY_CASH_POLICIES.find(
    (policy) => policy.value === buyCashPolicy
  );
  const activeBuyCashPolicy = (result?.buy_cash_policy || buyCashPolicy || 'above_target_reserves').toString();

  const formatCurrency = useCallback((value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }, []);

  const formatPercent = useCallback((value) => {
    if (value === null || value === undefined) return '-';
    return `${(value * 100).toFixed(1)}%`;
  }, []);

  // Load the tradeable symbol universe on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSymbolsLoading(true);
      setSymbolsError('');
      try {
        const data = await rrService.getDirectedTradeSymbols();
        if (!cancelled) setSymbols(data.symbols || []);
      } catch (error) {
        if (!cancelled) {
          setSymbolsError(error.response?.data?.detail || error.message || 'Failed to load symbols');
        }
      } finally {
        if (!cancelled) setSymbolsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load selectable Tamarac bulk reports for manual refresh.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBulkReportsLoading(true);
      setBulkReportsError('');
      try {
        const data = await rrService.getDirectedTradeBulkReportOptions();
        if (cancelled) return;
        const names = data?.report_names || [];
        setBulkReportOptions(names);
        setSelectedBulkReports(data?.default_report_names?.length ? data.default_report_names : names);
      } catch (error) {
        if (!cancelled) {
          setBulkReportsError(error.response?.data?.detail || error.message || 'Failed to load bulk report options');
        }
      } finally {
        if (!cancelled) setBulkReportsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Refresh the dedicated Directed Trade queue
  const fetchQueue = useCallback(async () => {
    try {
      const data = await rrService.getDirectedTradeQueue();
      const map = {};
      (data.queued || []).forEach((item) => {
        if (item.group_name) map[item.group_name] = item;
      });
      setQueuedTrades(map);
    } catch (error) {
      console.error('Error fetching directed trade queue:', error);
    }
  }, []);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  // Hydrate initial account data (cash/reserves + holdings) for the open group.
  // NOTE: groupBaseAccounts/groupBaseHoldings are intentionally NOT dependencies.
  // Setting them inside this effect would otherwise re-trigger the effect and cancel
  // the in-flight holdings fetch before it persists, causing an infinite refetch loop.
  useEffect(() => {
    if (!showDetailModal || !selectedGroup?.name) return;

    const groupName = selectedGroup.name;
    if (loadedGroupDataRef.current.has(groupName)) return;
    if (loadingGroupDataRef.current.has(groupName)) return;

    loadingGroupDataRef.current.add(groupName);

    let cancelled = false;
    (async () => {
      try {
        const snapshot = await rrService.getDailyNoActionClientDetail(groupName);
        const baseAccounts = snapshot?.account_allocations || [];

        const holdingsMap = {};
        await Promise.all(
          baseAccounts.map(async (account) => {
            const accountNumber = account.accountNumber || account.account_number;
            if (!accountNumber) return;
            try {
              const response = await rrService.getAccountHoldings(accountNumber);
              holdingsMap[accountNumber] = response?.holdings || [];
            } catch (err) {
              holdingsMap[accountNumber] = [];
            }
          })
        );

        if (cancelled) return;

        // Persist both pieces together so the guard never sees a half-loaded state.
        loadedGroupDataRef.current.add(groupName);
        setGroupBaseAccounts((prev) => (prev[groupName] ? prev : { ...prev, [groupName]: baseAccounts }));
        setGroupBaseHoldings((prev) => (prev[groupName] ? prev : { ...prev, [groupName]: holdingsMap }));
      } catch (err) {
        // If snapshot fetch fails, UI falls back to directed-trade detail payload.
      } finally {
        loadingGroupDataRef.current.delete(groupName);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDetailModal, selectedGroup]);

  // When the action changes, reset the mode to that action's default
  const handleActionChange = (newAction) => {
    setAction(newAction);
    setMode((newAction === 'buy' ? BUY_MODES : SELL_MODES)[0].value);
  };

  const handleRun = async () => {
    if (!symbol) {
      setRunError('Please select a symbol.');
      return;
    }
    setIsRunning(true);
    setRunError('');
    setRunProgress('Starting…');
    setResult(null);
    setAllocationOverrides({});
    setLiveTradesOverrides({});
    setActiveResultsTab('trades');
    setResultsSearch('');
    try {
      const data = await rrService.runDirectedTrade(
        symbol,
        action,
        mode,
        action === 'buy' ? buyCashPolicy : 'above_target_reserves',
        (snapshot) => {
        // Stream partial results so account families render as they finish.
        if (snapshot) {
          setResult(snapshot);
          const done = snapshot.completed_count || 0;
          const total = snapshot.total_group_count || 0;
          if (total > 0 && snapshot.status !== 'completed') {
            setRunProgress(`Processed ${done} of ${total} account families…`);
          } else if (snapshot.progress) {
            setRunProgress(snapshot.progress);
          }
        }
      }
      );
      setResult(data);
    } catch (error) {
      setRunError(error.response?.data?.detail || error.message || 'Failed to run directed trade');
    } finally {
      setIsRunning(false);
      setRunProgress('');
    }
  };

  const toggleBulkReportSelection = (reportName) => {
    setSelectedBulkReports((prev) => (
      prev.includes(reportName)
        ? prev.filter((name) => name !== reportName)
        : [...prev, reportName]
    ));
  };

  const handleFetchBulkReports = async () => {
    if (!selectedBulkReports.length) {
      setBulkFetchError('Select at least one report to fetch.');
      return;
    }

    setIsFetchingBulkReports(true);
    setBulkFetchError('');
    // Close the selector modal immediately and show progress on the page.
    setShowBulkReportsModal(false);
    setBulkFetchStatus({
      status: 'RUNNING',
      progress: 'Starting Tamarac report refresh…',
      total_reports: selectedBulkReports.length,
      completed_reports: 0,
    });

    try {
      const finalStatus = await rrService.startDirectedTradeBulkReportFetch(
        selectedBulkReports,
        {},
        (snapshot) => {
          if (snapshot) setBulkFetchStatus(snapshot);
        }
      );
      setBulkFetchStatus(finalStatus);
      await fetchQueue();
    } catch (error) {
      setBulkFetchError(error.response?.data?.detail || error.message || 'Failed to refresh Tamarac reports');
    } finally {
      setIsFetchingBulkReports(false);
    }
  };

  const openDetailModal = (row) => {
    setSelectedGroup(row);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    const groupName = selectedGroup?.name;
    setShowDetailModal(false);
    setSelectedGroup(null);
    // Discard ephemeral per-open edit state so the next open of this group starts
    // from the pristine `detail.*` payload. Re-feeding round-tripped trades/allocations
    // back into a freshly mounted child causes the equality guards to oscillate
    // ("Maximum update depth exceeded") on the second open.
    if (groupName) {
      setAllocationViewType('sleeve');
      setLiveTradesOverrides((prev) => {
        if (!(groupName in prev)) return prev;
        const next = { ...prev };
        delete next[groupName];
        return next;
      });
      setAllocationOverrides((prev) => {
        if (!(groupName in prev)) return prev;
        const next = { ...prev };
        delete next[groupName];
        return next;
      });
    }
  };

  // Queue the (possibly edited) trades for the open group
  const handleQueueTrades = async (trades, accountAllocations) => {
    if (!selectedGroup || !result) return;
    setIsQueuingTrades(true);
    try {
      await rrService.queueDirectedTrade({
        group_name: selectedGroup.name,
        symbol: result.symbol,
        action: result.action,
        mode: result.mode,
        trades,
        account_allocations: (accountAllocations || []).filter((a) => ((a.tradesCount || a.total_trades || 0) > 0)),
      });
      await fetchQueue();
      closeDetailModal();
    } catch (error) {
      console.error('Error queuing directed trades:', error);
      alert(`Error queuing trades: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsQueuingTrades(false);
    }
  };

  const handleClearQueued = async (groupName) => {
    if (!result) return;
    try {
      await rrService.deleteDirectedTradeQueueItem(result.symbol, groupName);
      await fetchQueue();
    } catch (error) {
      console.error('Error clearing queued directed trade:', error);
      alert(`Error clearing queued trade: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleDownloadOrders = async () => {
    setIsDownloading(true);
    try {
      const { blob, filename } = await rrService.downloadDirectedTradeOrders();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading directed trade orders:', error);
      alert(`Error downloading order files: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const queuedCount = Object.keys(queuedTrades).length;
  const totalQueuedTrades = useMemo(
    () => Object.values(queuedTrades).reduce((sum, item) => sum + (item.trades?.length || 0), 0),
    [queuedTrades]
  );
  const isBuyResult = useMemo(
    () => ((result?.action || action || '').toString().toLowerCase() === 'buy'),
    [result, action]
  );

  const summaryColumns = useMemo(() => [
    {
      key: 'queued_status',
      label: '',
      width: 50,
      minWidth: 50,
      maxWidth: 50,
      sortable: false,
      format: (value, row) => {
        const q = queuedTrades[row.name];
        if (!q) return null;
        return (
          <button
            className="btn btn-sm btn-outline-success"
            title={`Queued at ${q.queued_at ? new Date(q.queued_at).toLocaleString() : ''}`}
            onClick={(e) => { e.stopPropagation(); handleClearQueued(row.name); }}
            style={{ padding: '4px 8px', lineHeight: 1, fontSize: '0.95rem', minWidth: 40 }}
          >
            <i className="bi bi-clock-fill"></i>
          </button>
        );
      },
    },
    { key: 'name', label: 'Account Family', width: 320, sortable: true },
    {
      key: 'model_name',
      label: 'Model',
      width: 220,
      sortable: true,
      format: (value) => value || '-',
    },
    ...(isBuyResult ? [
      {
        key: 'starting_cash',
        label: 'Starting Cash',
        width: 150,
        sortable: true,
        align: 'right',
        format: (value) => formatCurrency(value),
      },
      {
        key: 'reserved_cash',
        label: 'Reserved Cash',
        width: 150,
        sortable: true,
        align: 'right',
        format: (value) => formatCurrency(value),
      },
      {
        key: 'spendable_cash',
        label: 'Spendable Cash',
        width: 150,
        sortable: true,
        align: 'right',
        format: (value) => formatCurrency(value),
      },
    ] : []),
    { key: 'trades', label: 'Trades', width: 90, sortable: true, align: 'center' },
    { key: 'value', label: 'Trade Value', width: 140, sortable: true, align: 'right', format: (value) => formatCurrency(value) },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [queuedTrades, formatCurrency, isBuyResult]);

  const noTradeColumns = useMemo(() => [
    { key: 'name', label: 'Account Family', width: 380, sortable: true },
    {
      key: 'model_name',
      label: 'Model',
      width: 220,
      sortable: true,
      format: (value) => value || '-',
    },
    ...(isBuyResult ? [
      {
        key: 'starting_cash',
        label: 'Starting Cash',
        width: 150,
        sortable: true,
        align: 'right',
        format: (value) => formatCurrency(value),
      },
      {
        key: 'reserved_cash',
        label: 'Reserved Cash',
        width: 150,
        sortable: true,
        align: 'right',
        format: (value) => formatCurrency(value),
      },
      {
        key: 'spendable_cash',
        label: 'Spendable Cash',
        width: 150,
        sortable: true,
        align: 'right',
        format: (value) => formatCurrency(value),
      },
    ] : []),
    {
      key: 'reason_label',
      label: 'Reason',
      width: 240,
      sortable: true,
      format: (value) => {
        const label = (value || 'No Cash').toString().trim();
        return <span className="fw-semibold">({label})</span>;
      },
    },
  ], [formatCurrency, isBuyResult]);

  const generateRowId = useCallback((row) => row.name, []);

  const filteredGroups = useMemo(() => {
    const groups = result?.groups || [];
    const query = resultsSearch.trim().toLowerCase();
    if (!query) return groups;

    return groups.filter((group) => {
      const name = (group?.name || '').toString().toLowerCase();
      const model = getGroupModelName(group).toLowerCase();
      return name.includes(query) || model.includes(query);
    });
  }, [result, resultsSearch]);

  const filteredNoTradeGroups = useMemo(() => {
    const groups = result?.no_trade_groups || [];
    const query = resultsSearch.trim().toLowerCase();
    if (!query) return groups;

    return groups.filter((group) => {
      const name = (group?.name || '').toString().toLowerCase();
      const model = getGroupModelName(group).toLowerCase();
      return name.includes(query) || model.includes(query);
    });
  }, [result, resultsSearch]);

  const sortableGroups = useMemo(
    () => filteredGroups.map((group) => ({
      ...group,
      model_name: getGroupModelName(group),
      starting_cash: getGroupStartingCash(group),
      reserved_cash: getGroupReservedCash(group, activeBuyCashPolicy),
      spendable_cash: getGroupSpendableCash(group, activeBuyCashPolicy),
      max_account_spendable_cash: getGroupMaxAccountSpendableCash(group, activeBuyCashPolicy),
    })),
    [filteredGroups, activeBuyCashPolicy]
  );

  const sortableNoTradeGroups = useMemo(
    () => filteredNoTradeGroups.map((group) => ({
      ...group,
      model_name: getGroupModelName(group),
      starting_cash: getGroupStartingCash(group),
      reserved_cash: getGroupReservedCash(group, activeBuyCashPolicy),
      spendable_cash: getGroupSpendableCash(group, activeBuyCashPolicy),
      max_account_spendable_cash: getGroupMaxAccountSpendableCash(group, activeBuyCashPolicy),
    })),
    [filteredNoTradeGroups, activeBuyCashPolicy]
  );

  const hasInsufficientOneShareReason = useMemo(
    () => filteredNoTradeGroups.some((group) => group?.reason_code === 'INSUFFICIENT_CASH_FOR_ONE_SHARE'),
    [filteredNoTradeGroups]
  );

  const renderDetail = () => {
    if (!selectedGroup) return null;
    const detail = selectedGroup.detail || {};
    const groupName = selectedGroup.name;
    const trades = liveTradesOverrides[groupName] || detail.trades || [];
    const hasSuggestedTrades = trades.length > 0;
    const directedAccountAllocations = detail.account_allocations || [];

    // Prefer backend allocations; only merge if we have baseline accounts to enhance with directed trades
    const baseAccounts = groupBaseAccounts[groupName] || [];
    const accountAllocations = directedAccountAllocations.length > 0
      ? directedAccountAllocations
      : baseAccounts;

    const holdingsByAccount = groupBaseHoldings[groupName] || {};

    const baseSecurityAllocations = detail.security_allocations || [];
    const derivedAllocations = buildAllocationGroups(holdingsByAccount, trades);
    const securityAllocations = allocationOverrides[groupName]?.security || (baseSecurityAllocations.length > 0 ? baseSecurityAllocations : derivedAllocations.security);
    const sleeveAllocations = detail.sleeve_allocations?.length > 0 ? detail.sleeve_allocations : derivedAllocations.sleeve;

    const filteredSecurityAllocations = (securityAllocations || []).filter((a) => a.name !== 'UNASSIGNED CASH');
    const filteredSleeveAllocations = (sleeveAllocations || []).filter((a) => a.name !== 'UNASSIGNED CASH');

    const allocationNames = [
      ...filteredSleeveAllocations.map((a) => a.name || a.sleeveName).filter(Boolean),
      ...filteredSecurityAllocations.map((a) => a.name || a.sleeveName).filter(Boolean),
    ];
    const uniqueNames = [...new Set(allocationNames)].sort((a, b) => a.localeCompare(b));
    const baseColors = ['#0d6efd', '#20c997', '#dc3545', '#ffc107', '#6f42c1', '#fd7e14', '#198754', '#0dcaf0', '#d63384', '#6610f2'];
    const colorMap = {};
    uniqueNames.forEach((name, index) => {
      if (index < baseColors.length) {
        colorMap[name] = baseColors[index];
      } else {
        const hue = (index * 137.5) % 360;
        colorMap[name] = `hsl(${hue}, 70%, 50%)`;
      }
    });

    const tradeType = (result?.action || detail.action || '').toLowerCase() === 'sell' ? 'sell' : 'buy';
    const accountTradesByNumber = trades.reduce((acc, trade) => {
      const accountNumber = trade.account_number || trade.AccountNumber || '';
      if (!accountNumber) return acc;
      if (!acc[accountNumber]) acc[accountNumber] = [];
      acc[accountNumber].push(trade);
      return acc;
    }, {});

    return (
      <div className="p-3 bg-light rounded">
        <div className="row mb-3">
          <div className="col-md-3">
            <div className="card h-100">
              <div className="card-header bg-light py-2 px-3">
                <h6 className="mb-0">
                  <i className="bi bi-clipboard-data me-2"></i>
                  Trade Summary
                </h6>
              </div>
              <div className="card-body py-2 px-3">
                <div className="d-flex flex-column gap-2">
                  {detail.model_name && (
                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                      <small className="text-muted">
                        <i className="bi bi-diagram-3 me-1"></i>
                        Model
                      </small>
                      <span className="fw-bold">{detail.model_name}</span>
                    </div>
                  )}

                  <div className="d-flex flex-column gap-1">
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-muted">Total Trades</small>
                      <strong>{detail.total_trades ?? trades.length ?? 0}</strong>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-muted">Trade Value</small>
                      <strong>{formatCurrency(detail.total_trade_value ?? 0)}</strong>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-muted">Cash Needs Met</small>
                      <strong>{formatPercent(detail.target_achievement)}</strong>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-muted">Cash Excess</small>
                      <strong className={(detail.cash_violations?.length || 0) > 0 ? 'text-warning' : 'text-success'}>
                        {detail.cash_violations?.length ?? 0}
                      </strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <CashSummaryCard
              accountAllocations={accountAllocations}
              allAccountHoldings={holdingsByAccount}
              trades={trades}
              isLoading={false}
              ignoreMargin={true}
            />
          </div>

          <div className="col-md-6">
            <PortfolioSummaryPieChart
              securityAllocations={filteredSecurityAllocations}
              accountAllocations={accountAllocations}
              trades={trades}
              isLoading={false}
              ignoreMargin={true}
            />
          </div>
        </div>

        {(filteredSleeveAllocations.length > 0 || filteredSecurityAllocations.length > 0) && (
          <AllocationVisualization
            sleeveAllocations={filteredSleeveAllocations}
            securityAllocations={filteredSecurityAllocations}
            colorMap={colorMap}
            viewType={allocationViewType}
            onViewTypeChange={setAllocationViewType}
          />
        )}

        {accountAllocations.length > 0 && (
          <div className="mb-3">
            <h6 className="mb-2">
              <i className="bi bi-wallet2 me-2"></i>
              Accounts ({accountAllocations.length})
            </h6>
            <div className="d-flex flex-wrap gap-2">
              {accountAllocations.map((account, idx) => {
                const accountNumber = account.accountNumber || account.account_number || '';
                const accountTrades = accountTradesByNumber[accountNumber] || [];
                const hasTrades = accountTrades.length > 0;
                const isNoTradeAccount = !hasTrades;
                const tradeCashImpact = accountTrades.reduce((sum, t) => {
                  const absVal = Math.abs(t.trade_value || t.value || t.TradeAmount || t.trade_amount || 0);
                  const actionName = (t.action || t.Action || '').toUpperCase();
                  const isBuy = actionName === 'BUY' || (!actionName && (t.quantity || 0) > 0);
                  return sum + (isBuy ? -absVal : absVal);
                }, 0);

                const displayCashCurrent = account.cashCurrent || account.cash_current || 0;
                const cashAfterTrades = displayCashCurrent + tradeCashImpact;
                const cashMin = account.cashReserveMin || account.cash_reserve_min || 0;
                const cashCenter = account.cashReserveCenter || account.cash_reserve_center || 0;
                const cashMax = account.cashReserveMax || account.cash_reserve_max || 0;
                const hasReserveTarget = cashCenter > 0 || cashMin > 0 || cashMax > 0;

                let cashStatus = 'In Range';
                let statusClass = 'bg-success';
                if (!hasReserveTarget) {
                  cashStatus = null;
                  statusClass = null;
                } else if (cashAfterTrades < cashMin) {
                  cashStatus = 'Below Min';
                  statusClass = 'bg-danger';
                } else if (cashAfterTrades > cashMax) {
                  cashStatus = 'Above Max';
                  statusClass = 'bg-warning text-dark';
                }

                const numAccounts = accountAllocations.length;
                const flexBasis = numAccounts <= 2 ? '48%' : numAccounts <= 3 ? '32%' : numAccounts <= 4 ? '24%' : numAccounts <= 6 ? '16%' : '15%';

                return (
                  <div
                    key={idx}
                    className={`card ${isNoTradeAccount ? 'border-secondary-subtle' : ''}`}
                    style={{
                      flex: `${isNoTradeAccount ? 0.82 : 1} 1 ${flexBasis}`,
                      minWidth: isNoTradeAccount ? '130px' : '150px',
                      borderLeft: `4px solid ${isNoTradeAccount ? '#adb5bd' : (tradeType === 'sell' ? '#dc3545' : '#198754')}`,
                      backgroundColor: isNoTradeAccount ? '#f8f9fa' : '#ffffff',
                      opacity: isNoTradeAccount ? 0.76 : 1,
                      filter: isNoTradeAccount ? 'saturate(0.75)' : 'none',
                    }}
                  >
                    <div className="card-body" style={{ padding: isNoTradeAccount ? '0.35rem 0.4rem' : '0.5rem' }}>
                      <div className="text-center border-bottom pb-1 mb-1">
                        <div className="fw-bold text-truncate" style={{ fontSize: isNoTradeAccount ? '0.78rem' : '0.85rem' }}>{account.accountName || account.account_name || 'Unknown Account'}</div>
                        <small className="text-muted" style={{ fontSize: isNoTradeAccount ? '0.62rem' : '0.7rem' }}>{accountNumber}</small>
                      </div>

                      <div className="text-center mb-1" style={{ fontSize: isNoTradeAccount ? '0.72rem' : '0.8rem' }}>
                        <div>
                          <span className="text-muted">Cash: </span>
                          <strong>{formatCurrency(displayCashCurrent)}</strong>
                        </div>
                        <div>
                          <span className="text-muted">After: </span>
                          <strong className={cashStatus === 'In Range' ? 'text-success' : cashStatus === 'Below Min' ? 'text-danger' : 'text-warning'}>
                            {formatCurrency(cashAfterTrades)}
                          </strong>
                        </div>
                      </div>

                      <div className="text-center mb-1" style={{ fontSize: isNoTradeAccount ? '0.62rem' : '0.7rem' }}>
                        <div className="text-muted">
                          {formatCurrency(cashMin)} - {formatCurrency(cashCenter)} - {formatCurrency(cashMax)}
                        </div>
                      </div>

                      <div className="text-center">
                        {cashStatus && (
                          <span className={`badge ${statusClass}`} style={{ fontSize: '0.65rem' }}>
                            {cashStatus}
                          </span>
                        )}
                        {hasTrades && (
                          <span className="badge bg-primary ms-1" style={{ fontSize: '0.65rem' }}>
                            {accountTrades.length} trade{accountTrades.length !== 1 ? 's' : ''}
                          </span>
                        )}
                        {isNoTradeAccount && (
                          <span className="badge bg-secondary-subtle text-secondary ms-1" style={{ fontSize: '0.62rem' }}>
                            no trades
                          </span>
                        )}
                      </div>

                      <AccountAllocationBars
                        holdings={holdingsByAccount[accountNumber] || []}
                        trades={accountTrades}
                        colorMap={colorMap}
                        viewType={allocationViewType}
                        cashReserveMax={cashMax}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {hasSuggestedTrades ? (
          <SuggestedTradesTable
            trades={trades}
            formatCurrency={formatCurrency}
            accountAllocations={accountAllocations}
            securityAllocations={baseSecurityAllocations}
            allAccountHoldings={holdingsByAccount}
            onQueueTrades={handleQueueTrades}
            isQueuingTrades={isQueuingTrades}
            showQueuedTrades={false}
            onQueueTriggerReady={(fn) => { queueTradesTriggerRef.current = fn; }}
            onAllocationsChange={(payload) => {
              const nextSecurity = payload?.security || [];
              setAllocationOverrides((prev) => {
                const prevSecurity = prev[groupName]?.security || [];
                if (areAllocationRowsEqual(prevSecurity, nextSecurity)) return prev;
                return { ...prev, [groupName]: { security: nextSecurity } };
              });
            }}
            onTradesChange={(lt) => {
              const nextTrades = lt || [];
              setLiveTradesOverrides((prev) => {
                const prevTrades = prev[groupName] || [];
                if (areTradesEqual(prevTrades, nextTrades)) return prev;
                return { ...prev, [groupName]: nextTrades };
              });
            }}
          />
        ) : (
          <div className="alert alert-secondary mb-0" role="status">
            <i className="bi bi-info-circle me-2"></i>
            No suggested trades for this account family. Review account cash/reserves,
            holdings, and restrictions to understand the no-trade recommendation.
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <i className="bi bi-cash-coin me-2"></i>
            Directed Trade
          </h2>
          <p className="text-muted mb-0">Buy or sell a single symbol across every account family.</p>
        </div>
        <div className="text-end d-flex flex-column align-items-end gap-2">
          <button
            className="btn btn-outline-primary"
            onClick={() => setShowBulkReportsModal(true)}
            disabled={isFetchingBulkReports || bulkReportsLoading}
            title="Select and refresh Tamarac bulk reports"
          >
            <i className="bi bi-cloud-download me-2"></i>
            Fetch Reports
          </button>
          {queuedCount > 0 && (
            <>
              <button
                className="btn btn-success"
                onClick={handleDownloadOrders}
                disabled={isDownloading}
                title={`Download order files for ${totalQueuedTrades} queued trades`}
              >
                <i className={`bi ${isDownloading ? 'bi-arrow-repeat spin' : 'bi-download'} me-2`}></i>
                Download Orders
                <span className="badge bg-light text-success ms-2">{totalQueuedTrades}</span>
              </button>
              <div className="text-muted small">
                <i className="bi bi-clock-fill text-success me-1"></i>
                <strong>{queuedCount}</strong> account famil{queuedCount === 1 ? 'y' : 'ies'} queued
              </div>
            </>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex flex-wrap flex-md-nowrap gap-3 align-items-end">
            <div style={{ flex: '2 1 0', minWidth: 0 }}>
              <label className="form-label fw-semibold">Symbol</label>
              {symbolsError ? (
                <div className="text-danger small">{symbolsError}</div>
              ) : (
                <SearchableDropdown
                  options={symbols}
                  value={symbol}
                  onChange={setSymbol}
                  placeholder={symbolsLoading ? 'Loading symbols...' : 'Select a symbol...'}
                  searchPlaceholder="Search symbols..."
                  disabled={symbolsLoading}
                />
              )}
            </div>
            <div style={{ flex: '0 0 130px' }}>
              <label className="form-label fw-semibold">Action</label>
              <select
                className="form-select"
                value={action}
                onChange={(e) => handleActionChange(e.target.value)}
              >
                <option value="buy">Buy</option>
                <option value="sell">Sell</option>
              </select>
            </div>
            <div style={{ flex: '1.6 1 0', minWidth: 0 }}>
              <label className="form-label fw-semibold">Mode</label>
              <select
                className="form-select"
                value={mode}
                onChange={(e) => setMode(e.target.value)}
              >
                {modeOptions.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            {action === 'buy' && (
              <div style={{ flex: '1.6 1 0', minWidth: 0 }}>
                <label className="form-label fw-semibold">Cash Spending Policy</label>
                <select
                  className="form-select"
                  value={buyCashPolicy}
                  onChange={(e) => setBuyCashPolicy(e.target.value)}
                >
                  {BUY_CASH_POLICIES.map((policy) => (
                    <option key={policy.value} value={policy.value}>{policy.label}</option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ flex: '0 0 130px' }} className="d-grid">
              <button
                className="btn btn-primary"
                onClick={handleRun}
                disabled={isRunning || !symbol}
              >
                {isRunning ? (
                  <><i className="bi bi-arrow-repeat spin me-2"></i>Running...</>
                ) : (
                  <><i className="bi bi-play-fill me-2"></i>Run</>
                )}
              </button>
            </div>
          </div>
          <div className="mt-2 text-muted small">
            <i className="bi bi-info-circle me-1"></i>
            {modeOptions.find((m) => m.value === mode)?.help}
            {action === 'buy' && selectedBuyCashPolicy ? ` ${selectedBuyCashPolicy.help}` : ''}
            {' '}Prioritizes Roth, then tax-deferred, then taxable accounts; respects trade restrictions and cash reserves.
          </div>
        </div>
      </div>

      <DirectedTradeReportsModal
        show={showBulkReportsModal}
        onClose={() => setShowBulkReportsModal(false)}
        reportOptions={bulkReportOptions}
        selectedReports={selectedBulkReports}
        onToggleReport={toggleBulkReportSelection}
        onSelectAll={() => setSelectedBulkReports(bulkReportOptions)}
        onClear={() => setSelectedBulkReports([])}
        onRefresh={handleFetchBulkReports}
        isFetching={isFetchingBulkReports}
        isLoading={bulkReportsLoading}
        loadError={bulkReportsError}
      />

      {bulkFetchError && (
        <div className="alert alert-danger" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>{bulkFetchError}
        </div>
      )}

      {bulkFetchStatus && (
        <div
          className={`alert d-flex align-items-center ${
            `${bulkFetchStatus.status || ''}`.toUpperCase() === 'SUCCEEDED'
              ? 'alert-success'
              : ['FAILED', 'TIMED_OUT', 'ABORTED'].includes(`${bulkFetchStatus.status || ''}`.toUpperCase())
                ? 'alert-danger'
                : 'alert-info'
          }`}
          role="status"
        >
          {`${bulkFetchStatus.status || ''}`.toUpperCase() === 'RUNNING' && (
            <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
          )}
          <div>
            <div className="fw-semibold">
              {bulkFetchStatus.progress || 'Refreshing Tamarac bulk reports...'}
            </div>
            <div className="small text-muted">
              {typeof bulkFetchStatus.completed_reports === 'number' && typeof bulkFetchStatus.total_reports === 'number'
                ? `${bulkFetchStatus.completed_reports} / ${bulkFetchStatus.total_reports} reports processed`
                : null}
              {bulkFetchStatus.current_state ? ` • Current step: ${bulkFetchStatus.current_state}` : ''}
            </div>
          </div>
        </div>
      )}

      {runError && (
        <div className="alert alert-danger" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>{runError}
        </div>
      )}

      {isRunning && (
        <div className="alert alert-info d-flex align-items-center" role="status">
          <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
          {runProgress || 'Running directed trade…'}
          <span className="ms-2 text-muted small">
            Results appear below as each account family finishes.
          </span>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="card">
          <div className="card-header bg-light d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              {result.action} {result.symbol}
              <span className="text-muted fw-normal ms-2" style={{ fontSize: '0.9rem' }}>
                {(isBuyResult ? BUY_MODES : SELL_MODES).find((m) => m.value === result.mode)?.label || result.mode}
              </span>
            </h5>
            <div className="d-flex gap-3 small text-muted">
              <span><strong>{result.total_groups || 0}</strong> with trades</span>
              <span><strong>{result.total_no_trade_groups || (result.no_trade_groups || []).length || 0}</strong> no trade</span>
              <span><strong>{result.total_trades || 0}</strong> trades</span>
              <span><strong>{formatCurrency(result.total_trade_value || 0)}</strong> total</span>
            </div>
          </div>
          <div className="card-body">
            <div className="row g-2 mb-3 align-items-end">
              <div className="col-md-6 col-lg-5">
                <label className="form-label fw-semibold mb-1">Search Account Family / Model</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Filter by account family or model..."
                  value={resultsSearch}
                  onChange={(e) => setResultsSearch(e.target.value)}
                />
              </div>
            </div>

            <ul className="nav nav-tabs mb-3">
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeResultsTab === 'trades' ? 'active' : ''}`}
                  onClick={() => setActiveResultsTab('trades')}
                >
                  Trades ({filteredGroups.length}{resultsSearch.trim() ? ` / ${(result.groups || []).length}` : ''})
                </button>
              </li>
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeResultsTab === 'noTrade' ? 'active' : ''}`}
                  onClick={() => setActiveResultsTab('noTrade')}
                >
                  No Trade ({filteredNoTradeGroups.length}{resultsSearch.trim() ? ` / ${(result.no_trade_groups || []).length}` : ''})
                </button>
              </li>
            </ul>

            {activeResultsTab === 'trades' ? (
              filteredGroups.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <i className="bi bi-inbox fs-3 d-block mb-2"></i>
                  {resultsSearch.trim()
                    ? 'No account families match the current search.'
                    : `No trades generated for ${result.symbol}. Check the No Trade tab for account family reasons.`}
                </div>
              ) : (
                <SortableResizableTable
                  columns={summaryColumns}
                  data={sortableGroups}
                  onRowClick={openDetailModal}
                  generateRowId={generateRowId}
                  emptyMessage="No account families with trades."
                />
              )
            ) : (
              filteredNoTradeGroups.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <i className="bi bi-check-circle fs-3 d-block mb-2"></i>
                  {resultsSearch.trim()
                    ? 'No account families match the current search.'
                    : 'Every account family generated at least one trade.'}
                </div>
              ) : (
                <>
                  {hasInsufficientOneShareReason && (
                    <div className="alert alert-secondary py-2 px-3 mb-2 small" role="note">
                      <i className="bi bi-info-circle me-2"></i>
                      For <strong>Insufficient Cash for 1 Share</strong>, Spendable Cash shows the
                      maximum spendable amount in any single eligible account (not the household sum).
                    </div>
                  )}
                  <SortableResizableTable
                    columns={noTradeColumns}
                    data={sortableNoTradeGroups}
                    onRowClick={openDetailModal}
                    generateRowId={generateRowId}
                    emptyMessage="No account families without trades."
                  />
                </>
              )
            )}
          </div>
        </div>
      )}

      {/* Detail modal */}
      {showDetailModal && selectedGroup && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable" style={{ maxWidth: '95%' }}>
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  <i className="bi bi-cash-coin me-2"></i>
                  {selectedGroup.name}
                  <span className="fw-normal ms-2" style={{ fontSize: '0.9rem' }}>
                    {result?.action} {result?.symbol}
                  </span>
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={closeDetailModal}></button>
              </div>
              <div className="modal-body">
                {renderDetail()}
              </div>
              <div className="modal-footer">
                {queuedTrades[selectedGroup?.name] && (
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={async () => {
                      if (!selectedGroup?.name) return;
                      if (!window.confirm(`Clear queued trades for ${selectedGroup.name}?`)) return;
                      await handleClearQueued(selectedGroup.name);
                    }}
                  >
                    <i className="bi bi-trash me-1"></i>
                    Clear Queued
                  </button>
                )}
                {(selectedGroup?.detail?.total_trades || selectedGroup?.detail?.trades?.length || 0) > 0 && (
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={() => {
                      if (queueTradesTriggerRef.current) {
                        queueTradesTriggerRef.current();
                      }
                    }}
                    disabled={isQueuingTrades}
                  >
                    {isQueuingTrades ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Queuing...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-clock-fill me-2"></i>
                        Queue Trades
                      </>
                    )}
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={closeDetailModal}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DirectedTrade;
